/**
 * 共通部品 (components/common/) の文言 — 日本語 (正本)。
 *
 * 管理画面・依頼者ポータルの両方で使われる部品の既定文言。依頼者ポータルは表示言語の
 * 切替の外 (LocaleProvider の外) にあるため、常にこの日本語で表示される。
 */
export const commonJa = {
  fileNaming: {
    original: '元の名前: {name}',
    manual: '手動で変更済み',
    unreadable: '読み取れませんでした',
    succeeded: '期間から名前を付けました',
    pending: 'ファイル名を読み取り中',
    read: '口座資料の名前を付け直す',
    queued: '{count}件を受け付けました。しばらくしてから再読み込みしてください。',
    readFailed: '名前の読み取りを開始できませんでした。再読み込みしてやり直してください。',
    hint: '未処理・読み取れなかった口座資料を読み取ります。手で変更した名前は上書きしません（1回20件まで）。',
    rename: 'ファイル名を変更',
    zip: '選択したファイルをZIPで保存',
    zipFailed: 'ZIPを作成できませんでした。選択を減らすか、個別に保存してください。',
  },
  actions: {
    cancel: 'キャンセル',
    confirm: '確認',
    close: '閉じる',
    search: '検索',
    loadMore: 'さらに読み込む',
  },
  status: {
    loading: '読み込み中',
    loadingEllipsis: '読み込み中...',
    searching: '検索中...',
  },
  empty: {
    noData: 'データがありません',
  },
  pagination: {
    previous: '前へ',
    next: '次へ',
  },
  table: {
    selectAllVisible: '表示中の行をすべて選択',
    selectRow: 'この行を選択',
  },
  search: {
    placeholder: '検索...',
  },
  /** 一覧の「自分の担当 / すべて」切替 (my-assigned-default-view-20261005 / ADR-108)。 */
  assignedView: {
    groupLabel: '表示する範囲',
    mine: '自分の担当',
    all: 'すべて',
    mineTitle: '担当弁護士または担当事務員が自分のものだけを表示します',
    allTitle: '閲覧できるものをすべて表示します',
    emptyMine: '自分の担当には該当するものがありません。',
    showAll: 'すべてを表示',
  },
  filter: {
    advanced: '詳細フィルター',
  },
  select: {
    placeholder: '選択してください',
    searchPlaceholder: '検索',
    noOptions: '該当する候補がありません',
    noMatch: '一致する候補がありません',
    moreAvailable: '候補がまだ残っています。検索語を追加するか、続きを読み込んでください。',
    clearSelection: '選択を解除',
    /** 全文プレビューのキーボード操作の案内 (読み上げ用) */
    previewScrollHint: 'PageUp / PageDown で全文をスクロールできます',
  },
  text: {
    showAll: '全文を表示',
    collapse: '閉じる',
  },
  date: {
    unparsable: '日付として読み取れませんでした (例: 2026/08/18)',
  },
  toast: {
    clickToOpenCase: 'クリックで該当案件を開く',
    clickToAction: 'クリックで{action}',
  },
  /**
   * どの画面からも出うる共通のエラーメッセージ。ToastContainer が表示するときだけ訳す
   * (エラーを作る側の文字列は変えない — `api/client.ts` は message の文字列で判定している箇所がある)。
   */
  knownMessages: {
    forbiddenAccess: 'アクセス権限がありません',
    forbiddenOperation: 'この操作を行う権限がありません',
    sessionExpired: 'セッションが期限切れです。再ログインしてください。',
    requestAborted: 'リクエストが中止されました',
    requestTimedOut: 'リクエストがタイムアウトしました ({seconds}秒)',
    copyFailed: 'コピーに失敗しました',
  },
  /**
   * よく使う語 (phase 2 の foundation で追加)。phase 2 の各 slice はこのファイルを編集しない —
   * 共通にしたい語はいったん自分の名前空間に置き、後で 1 本の整理 PR で上げる。
   */
  labels: {
    notSet: '未設定',
    required: '必須',
    optional: '任意',
    count: '件数',
    itemCount: '{count} 件',
    bulkActions: '一括操作',
    yes: 'はい',
    no: 'いいえ',
  },
  commonActions: {
    save: '保存',
    saving: '保存中...',
    delete: '削除',
    edit: '編集',
    back: '戻る',
    reload: '再読み込み',
    download: 'ダウンロード',
    upload: 'アップロード',
  },
  /**
   * 共有 util が返す日本語の sentinel を、描画の直前に訳すための文言 (`i18n/sharedLabels.ts`)。
   * 日本語は util の定数と同じ文字列 (`sharedLabels.test.ts` が一致を確かめる)。
   */
  shared: {
    assignee: {
      unset: '未設定',
      unresolved: '担当者未取得',
    },
    notSet: '未設定',
  },
  /** ファイルのプレビュー (`FilePreviewModal`)。 */
  filePreview: {
    dialogLabel: '{fileName} のプレビュー',
    zoomLevel: '表示倍率',
    fitToScreen: '画面に合わせる',
    fitShort: '画面調整',
    rotate: '回転',
    rotateLeft: '左に 90 度回転',
    rotateRight: '右に 90 度回転',
    rotateLeftShort: '左回転',
    rotateRightShort: '右回転',
    reset: 'リセット',
    resetView: '表示をリセット',
    resetTitle: '倍率と向きを初期状態に戻す',
    rename: 'ファイル名を変更',
    renameShort: 'ファイル名',
    controls: 'プレビュー操作',
    zoomIn: '拡大',
    zoomOut: '縮小',
    fetchFailed: 'ファイルの取得に失敗しました',
    imageFailed: '画像の直接表示に失敗しました',
    openInNewTab: '別タブで開く',
    unsupported: 'このファイル形式はプレビューに対応していません',
    downloadToView: 'ダウンロードして確認',
    /** 資料庫のフッターの日時と画素数の区切り */
    metaSeparator: ' ・ ',
  },
  /** 口座情報の入力欄 (`BankAccountFields`)。依頼者ポータルでは Provider の外なので日本語のまま。 */
  bankAccount: {
    institution: '金融機関名',
    institutionPlaceholder: '例: ゆうちょ銀行',
    branch: '支店名',
    branchPlaceholder: '例: ○○支店',
    accountType: '口座種別',
    accountTypeNone: '選択しない',
    accountTypes: {
      ordinary: '普通',
      current: '当座',
      savings: '貯蓄',
      other: 'その他',
    },
    accountNumber: '口座番号',
    accountNumberPlaceholder: '例: 1234567 (ゆうちょは 記号-番号)',
    accountNumberHint: '数字とハイフンだけを入力してください。全角や空白は自動で直します。',
  },
  /** 営業日の日付選択 (`BusinessDayPicker`)。 */
  businessDay: {
    placeholder: '日付を選択',
    previousMonth: '前の月',
    nextMonth: '次の月',
    dayUnavailable: '{date} (選べません)',
  },
  /** 未保存の変更の帯 (`StickySaveBar`)。既定の文言はメッセージ設定の画面向け。 */
  stickySave: {
    region: '未保存の変更',
    title: '未保存の変更があります',
    description:
      'このページの編集内容はまだ保存されていません。保存すると、依頼者ポータル・通知・各種メッセージに反映されます。',
    note: '※ 保存はメッセージ設定全体に適用されます（上記のすべてのセクションが対象）。',
    save: 'すべてのメッセージ設定を保存',
    discard: '変更を破棄',
    changeCount: '{count} 件の変更',
  },
  /** ファイル名の変更 (`FileRenameModal`)。 */
  fileRename: {
    title: 'ファイル名を変更',
    label: 'ファイル名',
    placeholder: 'ファイル名 (拡張子を除く)',
    hint: '拡張子は変更できません。表示名のみを変更し、ファイルの中身は変わりません。',
  },
  /** ファイルのアップロード欄 (`FileUpload`)。 */
  fileUpload: {
    tooLarge: 'ファイルサイズが上限 ({maxSizeMB}MB) を超えています',
    failed: 'アップロードに失敗しました',
    uploading: 'アップロード中...',
    dropHint: 'ドラッグ&ドロップ または クリックしてファイルを選択',
    maxSize: '最大 {maxSizeMB}MB',
  },
  /** CloudSign の署名ステータス (`CloudSignStatusBadge`)。 */
  cloudSign: {
    draft: '下書き',
    sent: '署名待ち',
    signed: '署名済み',
    canceled: 'キャンセル',
    template: 'テンプレート',
    imported: '取込文書',
    unknown: '不明',
  },
} as const;
